// src/pages/PrivacyPolicyPage.jsx
import React from "react";
import { SCHOOL } from "../config";

export default function PrivacyPolicyPage() {
  return (
    <div style={{ maxWidth: "760px", margin: "0 auto", padding: "40px 20px 80px", lineHeight: 1.7, color: "#1F2937" }}>
      <h1 style={{ color: "#7B1515", fontSize: "1.6rem", marginBottom: "4px" }}>Privacy Policy</h1>
      <p style={{ color: "#6B7280", fontSize: ".85rem", marginBottom: "28px" }}>
        Last updated: September 2026 · Applies to the {SCHOOL.name} Student Portal (web and Android)
      </p>

      <p>
        {SCHOOL.name} ("we", "us", "our") operates the Student Portal — a website and Android app used by
        students and their parents/guardians to manage class registration, attendance, course materials, and fees.
        This policy explains what information we collect, why, and how it's handled.
      </p>

      <h2 style={sectionStyle}>Who this applies to</h2>
      <p>
        Many of our students are minors. Where a student is a minor, their parent or guardian registers on the
        student's behalf and is the account holder — references to "you" below mean whoever controls the account
        (the student, or their parent/guardian).
      </p>

      <h2 style={sectionStyle}>Information we collect</h2>
      <ul style={listStyle}>
        <li><strong>Registration details:</strong> full name, date of birth, email, phone number, alternate phone, home address.</li>
        <li><strong>Profile photo:</strong> optional, used to identify the student within the portal and to admin staff.</li>
        <li><strong>Enrollment details:</strong> subject, ABGMM exam level, assigned course and batch, notes you provide at registration.</li>
        <li><strong>Fee and payment records:</strong> amounts, payment dates, payment method, and — for online payments — a
          transaction/order ID from our payment processor, Cashfree. We do not receive or store your card, UPI, or
          bank account details; those are handled entirely by Cashfree.</li>
        <li><strong>Attendance and course progress:</strong> which course content has been unlocked for you, and course-completion history.</li>
        <li><strong>Push notification token:</strong> if you allow notifications, a device token used to deliver fee reminders
          and announcements. You can revoke this at any time in your device's notification settings.</li>
        <li><strong>Account credentials:</strong> your email/password (stored securely by Firebase Authentication, we never
          see your raw password) or, if you choose, your Google account identity via Google Sign-In.</li>
      </ul>

      <h2 style={sectionStyle}>How we use this information</h2>
      <ul style={listStyle}>
        <li>To register and manage your enrollment, course assignment, and attendance.</li>
        <li>To track and reconcile fee payments, and to send you a receipt.</li>
        <li>To send you fee reminders, class announcements, and updates to content you're enrolled in — by
          email and/or push notification.</li>
        <li>To respond to questions you send us directly.</li>
      </ul>
      <p>We do not sell your information, and we do not use it for advertising.</p>

      <h2 style={sectionStyle}>Who we share it with</h2>
      <p>We share information only with the service providers needed to run the portal:</p>
      <ul style={listStyle}>
        <li><strong>Google Firebase</strong> — authentication, database, file storage, and hosting for the portal and app.</li>
        <li><strong>Cashfree Payments</strong> — processes online fee payments. We send them the amount and your name/phone/email
          to create a payment session; we never see your payment instrument details.</li>
        <li><strong>Zoho Mail</strong> — delivers transactional emails (receipts, account notices) on our behalf.</li>
      </ul>
      <p>We do not share your information with any other third party, and we do not permit these providers to use
        your data for their own purposes.</p>

      <h2 style={sectionStyle}>How long we keep it</h2>
      <p>
        We retain your registration and fee records for as long as you're enrolled, and afterward for as long as
        needed to keep accurate financial and attendance records. You can ask us to delete your account and
        associated data at any time — see Contact below — and we'll do so unless we're required to retain
        financial records for a longer period by law.
      </p>

      <h2 style={sectionStyle}>Your choices</h2>
      <ul style={listStyle}>
        <li>You can update your profile photo, phone number, and password at any time from the Profile page.</li>
        <li>You can turn off push notifications from your device settings, or by declining the notification permission prompt.</li>
        <li>You can ask us to export, correct, or delete your data by contacting us (below).</li>
      </ul>

      <h2 style={sectionStyle}>Security</h2>
      <p>
        Your data is stored with Google Firebase, protected by access rules that restrict each account to its own
        records (and, for family/sibling accounts registered under one login, to the records of those linked
        profiles). Passwords are never stored by us in plain text — Firebase Authentication handles this.
      </p>

      <h2 style={sectionStyle}>Changes to this policy</h2>
      <p>
        If we make material changes to this policy, we'll update the date at the top of this page and, where
        appropriate, notify you in the portal.
      </p>

      <h2 style={sectionStyle}>Contact us</h2>
      <p>
        For any privacy question, or to request access to, correction of, or deletion of your data, contact:<br />
        📧 <a href={`mailto:${SCHOOL.email}`} style={{ color: "#7B1515" }}>{SCHOOL.email}</a><br />
        📞 {SCHOOL.phone}<br />
        📍 {SCHOOL.address}
      </p>
    </div>
  );
}

const sectionStyle = { color: "#7B1515", fontSize: "1.1rem", marginTop: "28px", marginBottom: "8px" };
const listStyle = { paddingLeft: "20px" };
